import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Switch } from './ui/switch';

const EMPTY_SET = new Set();

export const ClientPersonalTab = ({ clientId, api: providedApi, user: providedUser }) => {
  const auth = useAuth();
  const api = providedApi || auth.api;
  const user = providedUser || auth.user;
  const { t } = useTranslation();
  const [staff, setStaff] = useState([]);
  const [vetoedIds, setVetoedIds] = useState(EMPTY_SET);
  const [state, setState] = useState('LOADING');
  const [savingId, setSavingId] = useState(null);

  const loadData = useCallback(async () => {
    if (!clientId || !api) return;
    setState('LOADING');
    try {
      const [staffResponse, vetoResponse] = await Promise.all([
        api.get('/users?role=STAFF&active=true'),
        api.get(`/clients/${clientId}/staff-vetos`),
      ]);
      const nextStaff = Array.isArray(staffResponse.data) ? staffResponse.data : [];
      const nextVetoedIds = new Set(
        (vetoResponse.data?.vetos || []).map((veto) => veto.staffId).filter(Boolean),
      );
      setStaff(nextStaff);
      setVetoedIds(nextVetoedIds);
      setState(nextStaff.length ? 'SUCCESS_NONEMPTY' : 'SUCCESS_EMPTY');
    } catch (error) {
      setStaff([]);
      setVetoedIds(EMPTY_SET);
      setState('ERROR');
    }
  }, [api, clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggle = useCallback(async (staffId) => {
    if (!clientId || !api || savingId) return;
    const currentlyVetoed = vetoedIds.has(staffId);
    setSavingId(staffId);
    try {
      await api.put(`/clients/${clientId}/staff-vetos`, {
        add: currentlyVetoed ? [] : [staffId],
        remove: currentlyVetoed ? [staffId] : [],
      });
      await loadData();
    } catch (error) {
      setState('ERROR');
    } finally {
      setSavingId(null);
    }
  }, [api, clientId, loadData, savingId, vetoedIds]);

  if (state === 'LOADING') {
    return (
      <Card data-testid="personal-loading">
        <CardContent className="pt-6">{t('clientProfile.personal.loading')}</CardContent>
      </Card>
    );
  }

  if (state === 'ERROR') {
    return (
      <Card data-testid="personal-error">
        <CardContent className="pt-6 space-y-3">
          <p className="text-sm text-red-500">{t('clientProfile.personal.error')}</p>
          <Button type="button" variant="outline" onClick={loadData}>
            {t('common.retry') || 'Retry'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (state === 'SUCCESS_EMPTY') {
    return (
      <Card data-testid="personal-empty">
        <CardContent className="pt-6 text-sm text-zinc-500">
          {t('clientProfile.personal.emptyStaff')}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="personal-content">
      <CardHeader>
        <CardTitle>{t('clientProfile.tabs.personal')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3" data-testid="personal-list">
          {staff.map((member) => {
            const isVetoed = vetoedIds.has(member.id);
            return (
              <li key={member.id} data-testid={`personal-row-${member.id}`} className="flex items-center justify-between p-3 border border-zinc-800 rounded-sm">
                <div>
                  <div className="font-medium">{member.fullName || member.name}</div>
                  <div className="text-xs text-zinc-500">{isVetoed ? 'Vetoed' : 'Allowed'}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Label htmlFor={`veto-${member.id}`} className="text-sm">
                    {isVetoed ? 'Vetoed' : 'Allowed'}
                  </Label>
                  <Switch
                    id={`veto-${member.id}`}
                    checked={isVetoed}
                    disabled={Boolean(savingId)}
                    onCheckedChange={() => handleToggle(member.id)}
                    data-testid={`veto-switch-${member.id}`}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

export default ClientPersonalTab;
